/**
 * Export PDF de la facture, construit à partir du MODÈLE de données (jamais
 * depuis le DOM) avec pdfmake.
 *
 * Mise en page : filet d'accent bleu, logo seul en en-tête, destinataire,
 * tableau à en-tête bleu, totaux alignés à droite, et coordonnées complètes de
 * l'émetteur en pied de page — l'en-tête reste ainsi dégagé.
 *
 * Contrat de fidélité avec FactureApercu.vue : A4 portrait, marges de 40 pt,
 * police Roboto (celle du vfs pdfmake), mêmes tailles et mêmes gris. Un écart
 * ici rendrait l'aperçu menteur.
 */

import {normaliser, intituleFacture, COULEUR_ACCENT} from './modele'
import {formaterMontant, calculerTotaux} from './calculs'

const LARGEUR_UTILE = 515
const GRIS_TEXTE = '#333333'
const GRIS_DOUX = '#777777'
const GRIS_FILET = '#dddddd'
const FOND_PANNEAU = '#f4f8fb'

function chargerPdfMake () {
  // require dans la fonction : pdfmake et ses polices ne sont évalués qu'au
  // premier export, pas au chargement de l'application
  const pdfMake = require('pdfmake/build/pdfmake')
  const polices = require('pdfmake/build/vfs_fonts')
  // vfs_fonts.js n'exporte rien : il écrit dans `this.pdfMake`, qui vaut
  // module.exports sous webpack comme sous Node
  if (!pdfMake.vfs && polices && polices.pdfMake) {
    pdfMake.vfs = polices.pdfMake.vfs
  }
  return pdfMake
}

function lignesAdresse (bloc) {
  const lignes = []
  if (bloc.adresse) lignes.push(bloc.adresse)
  if (bloc.cp || bloc.ville) lignes.push(((bloc.cp || '') + ' ' + (bloc.ville || '')).trim())
  if (bloc.pays) lignes.push(bloc.pays)
  if (bloc.email) lignes.push(bloc.email)
  if (bloc.numEntreprise) lignes.push('N° TVA : ' + bloc.numEntreprise)
  return lignes
}

/**
 * Coordonnées de l'émetteur condensées pour le pied de page : deux lignes
 * séparées par des points médians, plutôt qu'une colonne qui encombrerait
 * l'en-tête.
 */
export function lignesPiedEmetteur (emetteur) {
  const bloc = emetteur || {}
  const identite = [
    bloc.nom,
    bloc.adresse,
    ((bloc.cp || '') + ' ' + (bloc.ville || '')).trim(),
    bloc.pays
  ].filter(Boolean).join(' · ')

  const contact = [
    bloc.email,
    bloc.telephone,
    bloc.numEntreprise ? 'N° TVA : ' + bloc.numEntreprise : null
  ].filter(Boolean).join(' · ')

  return [identite, contact].filter(Boolean)
}

function construireEntete (facture, logoDataUrl) {
  const logoVisible = facture.options.logo && !!logoDataUrl

  // Le logo porte déjà la dénomination : la répéter chargerait l'en-tête.
  // Sans logo, il faut bien que l'émetteur soit identifié ici.
  const colonneGauche = logoVisible
    ? {image: logoDataUrl, fit: [200, 62]}
    : {text: facture.emetteur.nom || '', style: 'nomEmetteur'}

  const meta = [
    [{text: 'Numéro', style: 'metaLibelle'}, {text: facture.numero || '—', style: 'metaValeur'}],
    [{text: 'Émise le', style: 'metaLibelle'}, {text: facture.dateEmission || '', style: 'metaValeur'}]
  ]
  if (facture.options.echeance && facture.dateEcheance) {
    meta.push([
      {text: 'Échéance', style: 'metaLibelle'},
      {text: facture.dateEcheance, style: 'metaValeur'}
    ])
  }
  if (facture.options.reference && facture.reference) {
    meta.push([
      {text: 'Référence', style: 'metaLibelle'},
      {text: facture.reference, style: 'metaValeur'}
    ])
  }

  return {
    columns: [
      {width: '*', stack: [colonneGauche]},
      {
        width: 210,
        stack: [
          {text: 'FACTURE', style: 'titre', color: COULEUR_ACCENT},
          {
            table: {widths: ['*', 'auto'], body: meta},
            layout: 'noBorders',
            margin: [0, 4, 0, 0]
          }
        ]
      }
    ],
    margin: [0, 0, 0, 26]
  }
}

function construireClient (facture) {
  const stack = [
    {text: facture.client.nom || '', style: 'nomClient'}
  ]
  const adresse = lignesAdresse(facture.client)
  if (adresse.length) {
    stack.push({text: adresse.join('\n'), style: 'petit', margin: [0, 3, 0, 0]})
  }
  return {stack: stack, margin: [0, 0, 0, 22]}
}

function construireTableauLignes (facture, totaux, devise) {
  // La colonne de TVA est affichée dès que la TVA s'applique : en taux unique,
  // chaque ligne rappelle le même taux, ce qui rend la facture lisible seule
  const colonneTva = facture.options.tva

  const entetes = [
    {text: 'Description', style: 'enteteCellule'},
    {text: 'Qté', style: 'enteteCellule', alignment: 'right'},
    {text: 'Prix', style: 'enteteCellule', alignment: 'right'}
  ]
  if (colonneTva) entetes.push({text: '% TVA', style: 'enteteCellule', alignment: 'right'})
  entetes.push({text: 'Total', style: 'enteteCellule', alignment: 'right'})

  if (facture.template === 'minimal') {
    entetes.forEach(function (cellule) { cellule.color = COULEUR_ACCENT })
  } else {
    entetes.forEach(function (cellule) {
      cellule.fillColor = COULEUR_ACCENT
      cellule.color = '#ffffff'
    })
  }

  const corps = [entetes]
  facture.lignes.forEach(function (ligne, index) {
    const cellules = [
      {text: ligne.description || '', style: 'cellule'},
      {text: String(ligne.quantite), style: 'cellule', alignment: 'right'},
      {text: formaterMontant(ligne.prixUnitaire, devise), style: 'cellule', alignment: 'right'}
    ]
    if (colonneTva) {
      const taux = facture.tva.mode === 'ligne' ? ligne.tauxTva : facture.tva.taux
      cellules.push({text: taux + ' %', style: 'cellule', alignment: 'right'})
    }
    cellules.push({
      text: formaterMontant(totaux.montantsLignes[index], devise),
      style: 'cellule',
      alignment: 'right'
    })
    corps.push(cellules)
  })

  const largeurs = colonneTva ? ['*', 34, 70, 48, 78] : ['*', 40, 80, 85]

  return {
    table: {headerRows: 1, widths: largeurs, body: corps, dontBreakRows: true},
    layout: dispositionTableau(facture.template),
    margin: [0, 0, 0, 14]
  }
}

/**
 * Une disposition de tableau par modèle : c'est elle qui donne son caractère à
 * chaque template (aplat de couleur, grille complète, ou filets minimaux).
 */
function dispositionTableau (template) {
  if (template === 'classique') {
    return {
      hLineWidth: function () { return 0.5 },
      vLineWidth: function () { return 0.5 },
      hLineColor: function () { return GRIS_FILET },
      vLineColor: function () { return GRIS_FILET },
      paddingTop: function () { return 5 },
      paddingBottom: function () { return 5 }
    }
  }

  if (template === 'minimal') {
    return {
      hLineWidth: function (index) { return index === 1 ? 1.4 : (index === 0 ? 0 : 0.4) },
      vLineWidth: function () { return 0 },
      hLineColor: function (index) { return index === 1 ? COULEUR_ACCENT : '#f0f0f0' },
      paddingLeft: function (index) { return index === 0 ? 0 : 6 },
      paddingTop: function () { return 6 },
      paddingBottom: function () { return 6 }
    }
  }

  return {
    hLineWidth: function (index) { return index <= 1 ? 0 : 0.5 },
    vLineWidth: function () { return 0 },
    hLineColor: function () { return '#eeeeee' },
    paddingTop: function () { return 6 },
    paddingBottom: function () { return 6 }
  }
}

function construireTotaux (facture, totaux, devise) {
  const corps = []

  if (facture.options.tva || facture.options.remise || facture.options.frais) {
    corps.push([
      {text: 'Sous-total', style: 'totalLibelle'},
      {text: formaterMontant(totaux.sousTotal, devise), style: 'totalValeur'}
    ])
  }
  if (facture.options.remise) {
    const suffixe = facture.remise.type === 'pourcentage' ? ' (' + facture.remise.valeur + ' %)' : ''
    corps.push([
      {text: 'Remise' + suffixe, style: 'totalLibelle'},
      {text: '- ' + formaterMontant(totaux.montantRemise, devise), style: 'totalValeur'}
    ])
  }
  if (facture.options.tva) {
    // Le taux est toujours affiché, y compris à 0 % : la TVA doit se lire
    // explicitement sur la facture
    const suffixe = facture.tva.mode === 'global' ? ' (' + facture.tva.taux + ' %)' : ''
    corps.push([
      {text: 'TVA' + suffixe, style: 'totalLibelle'},
      {text: formaterMontant(totaux.montantTva, devise), style: 'totalValeur'}
    ])
  }
  if (facture.options.frais) {
    corps.push([
      {text: 'Frais', style: 'totalLibelle'},
      {text: formaterMontant(totaux.frais, devise), style: 'totalValeur'}
    ])
  }
  corps.push([
    {text: 'Total', style: 'totalFinalLibelle', color: COULEUR_ACCENT},
    {text: formaterMontant(totaux.total, devise), style: 'totalFinalValeur', color: COULEUR_ACCENT}
  ])

  const derniereLigne = corps.length

  return {
    columns: [
      {width: '*', text: ''},
      {
        width: 250,
        table: {widths: ['*', 'auto'], body: corps},
        layout: {
          hLineWidth: function (index) { return index === derniereLigne - 1 ? 1.2 : 0 },
          vLineWidth: function () { return 0 },
          hLineColor: function () { return COULEUR_ACCENT },
          paddingTop: function () { return 3 },
          paddingBottom: function () { return 3 }
        }
      }
    ],
    margin: [0, 0, 0, 18]
  }
}

function construireMentions (facture, qrDataUrl) {
  const blocs = []

  if (facture.options.tva && facture.tva.mentionExoneration) {
    blocs.push({text: facture.tva.mentionExoneration, style: 'mention', margin: [0, 0, 0, 10]})
  }

  const paiementRempli = facture.options.paiement && (
    facture.paiement.beneficiaire || facture.paiement.iban ||
    facture.paiement.bic || facture.paiement.communicationStructuree
  )

  if (paiementRempli || (facture.options.qr && qrDataUrl)) {
    const stackPaiement = [{text: 'PAIEMENT', style: 'etiquette', color: COULEUR_ACCENT}]
    if (facture.paiement.beneficiaire) {
      stackPaiement.push({text: facture.paiement.beneficiaire, style: 'paiementValeur'})
    }
    if (facture.paiement.iban) {
      stackPaiement.push({text: 'IBAN : ' + facture.paiement.iban, style: 'paiementValeur'})
    }
    if (facture.paiement.bic) {
      stackPaiement.push({text: 'BIC : ' + facture.paiement.bic, style: 'paiementValeur'})
    }
    if (facture.paiement.communicationStructuree) {
      stackPaiement.push({
        text: 'Communication : ' + facture.paiement.communicationStructuree,
        style: 'paiementValeur'
      })
    }

    const colonnes = [{width: '*', stack: stackPaiement}]
    if (facture.options.qr && qrDataUrl) {
      // Le QR est tenu dans le bloc, légendé : seul au milieu de la page il
      // ressemblait à une image déposée au hasard
      colonnes.push({
        width: 78,
        stack: [
          {image: qrDataUrl, fit: [78, 78]},
          {text: 'Scannez pour payer', style: 'legendeQr'}
        ]
      })
    }

    // Panneau teinté avec un filet d'accent à gauche : le paiement se repère
    // d'un coup d'œil sur la facture imprimée
    blocs.push({
      table: {
        widths: ['*'],
        body: [[{fillColor: FOND_PANNEAU, columns: colonnes, columnGap: 18}]]
      },
      layout: {
        hLineWidth: function () { return 0 },
        vLineWidth: function (index) { return index === 0 ? 3 : 0 },
        vLineColor: function () { return COULEUR_ACCENT },
        paddingLeft: function () { return 14 },
        paddingRight: function () { return 14 },
        paddingTop: function () { return 12 },
        paddingBottom: function () { return 12 }
      },
      margin: [0, 0, 0, 14]
    })
  }

  if (facture.options.conditions && facture.conditionsPaiement) {
    blocs.push({text: 'CONDITIONS', style: 'etiquette', color: COULEUR_ACCENT})
    blocs.push({text: facture.conditionsPaiement, style: 'petit', margin: [0, 0, 0, 10]})
  }

  if (facture.options.notes && facture.notes) {
    blocs.push({text: 'NOTES', style: 'etiquette', color: COULEUR_ACCENT})
    blocs.push({text: facture.notes, style: 'petit'})
  }

  return blocs
}

/**
 * Les blocs d'une facture, sans enveloppe de document : réutilisés tels quels
 * pour un export isolé et pour un lot où les factures s'enchaînent.
 */
function contenuFacture (facture, totaux, qrDataUrl, logoDataUrl) {
  const devise = facture.devise
  const contenu = []

  if (facture.template === 'moderne') {
    contenu.push({
      canvas: [{type: 'rect', x: 0, y: 0, w: LARGEUR_UTILE, h: 6, color: COULEUR_ACCENT}],
      margin: [0, 0, 0, 18]
    })
  }

  contenu.push(construireEntete(facture, logoDataUrl))

  if (facture.template === 'classique') {
    contenu.push({
      canvas: [{
        type: 'line', x1: 0, y1: 0, x2: LARGEUR_UTILE, y2: 0,
        lineWidth: 0.5, lineColor: GRIS_FILET
      }],
      margin: [0, 0, 0, 18]
    })
  }

  contenu.push(construireClient(facture))
  contenu.push(construireTableauLignes(facture, totaux, devise))
  contenu.push(construireTotaux(facture, totaux, devise))
  construireMentions(facture, qrDataUrl).forEach(function (bloc) {
    contenu.push(bloc)
  })

  return contenu
}

/**
 * Enveloppe commune : format, marges, pied de page et styles. L'émetteur du
 * pied est celui de la première facture — c'est toujours la fédération.
 */
function enveloppeDocument (facture, contenu, titre) {
  return {
    pageSize: 'A4',
    pageMargins: [40, 40, 40, 84],
    info: {
      title: titre,
      author: facture.emetteur.nom || 'ABFT'
    },
    defaultStyle: {fontSize: 9, color: GRIS_TEXTE, lineHeight: 1.25},
    content: contenu,
    footer: function (page, pages) {
      const blocs = [{
        canvas: [{
          type: 'line', x1: 0, y1: 0, x2: LARGEUR_UTILE, y2: 0,
          lineWidth: 0.5, lineColor: GRIS_FILET
        }],
        margin: [0, 0, 0, 7]
      }]

      lignesPiedEmetteur(facture.emetteur).forEach(function (ligne) {
        blocs.push({text: ligne, alignment: 'center', style: 'piedDiscret'})
      })

      if (pages > 1) {
        blocs.push({
          text: 'Page ' + page + ' / ' + pages,
          alignment: 'center',
          style: 'piedDiscret',
          margin: [0, 5, 0, 0]
        })
      }

      return {stack: blocs, margin: [40, 14, 40, 0]}
    },
    styles: {
      titre: {
        fontSize: facture.template === 'minimal' ? 16 : 22,
        bold: facture.template !== 'classique',
        characterSpacing: facture.template === 'classique' ? 4 : 1,
        alignment: 'right'
      },
      nomEmetteur: {fontSize: 13, bold: true},
      nomClient: {fontSize: 11, bold: true},
      etiquette: {fontSize: 8, bold: true, characterSpacing: 1, margin: [0, 0, 0, 3]},
      petit: {fontSize: 8.5, color: GRIS_DOUX},
      metaLibelle: {fontSize: 8.5, color: GRIS_DOUX, alignment: 'right'},
      metaValeur: {fontSize: 8.5, bold: true, alignment: 'right'},
      enteteCellule: {fontSize: 9, bold: true},
      cellule: {fontSize: 9},
      totalLibelle: {fontSize: 9, color: GRIS_DOUX},
      totalValeur: {fontSize: 9, alignment: 'right'},
      totalFinalLibelle: {fontSize: 11.5, bold: true, margin: [0, 3, 0, 0]},
      totalFinalValeur: {fontSize: 11.5, bold: true, alignment: 'right', margin: [0, 3, 0, 0]},
      mention: {fontSize: 8.5, italics: true, color: GRIS_DOUX},
      paiementValeur: {fontSize: 9},
      legendeQr: {fontSize: 6.5, color: GRIS_DOUX, alignment: 'center', margin: [0, 3, 0, 0]},
      piedDiscret: {fontSize: 7.5, color: GRIS_DOUX}
    }
  }
}

export function construireDocDefinition (factureBrute, totaux, qrDataUrl, logoDataUrl) {
  const facture = normaliser(factureBrute)
  return enveloppeDocument(
    facture,
    contenuFacture(facture, totaux, qrDataUrl, logoDataUrl),
    intituleFacture(facture.numero)
  )
}

/**
 * Toutes les factures d'un lot dans un seul document, une par page : un fichier
 * unique se relit d'une traite et s'imprime en une fois, là où cinquante
 * téléchargements séparés sont ingérables.
 */
export function construireDocDefinitionLot (facturesBrutes, parametres) {
  const liste = (facturesBrutes || []).map(normaliser)
  if (!liste.length) return null

  const options = parametres || {}
  const qrParFacture = options.qrParFacture || {}
  const contenu = []

  liste.forEach(function (facture, index) {
    const blocs = contenuFacture(
      facture,
      calculerTotaux(facture),
      qrParFacture[facture.id] || null,
      options.logoDataUrl || null
    )
    if (index > 0 && blocs.length) {
      // Chaque facture repart sur une page neuve
      blocs[0] = Object.assign({}, blocs[0], {pageBreak: 'before'})
    }
    blocs.forEach(function (bloc) { contenu.push(bloc) })
  })

  return enveloppeDocument(liste[0], contenu, 'Factures')
}

/**
 * Le PDF d'une facture en base64, pour être confié à l'API. C'est exactement
 * le document que l'utilisateur a relu à l'écran : le serveur ne le regénère
 * pas, il le stocke et le joint tel quel.
 */
export function factureEnBase64 (facture, totaux, qrDataUrl, logoDataUrl) {
  return new Promise(function (resoudre, rejeter) {
    try {
      const pdfMake = chargerPdfMake()
      const definition = construireDocDefinition(facture, totaux, qrDataUrl, logoDataUrl)
      pdfMake.createPdf(definition).getBase64(function (donnees) { resoudre(donnees) })
    } catch (erreur) {
      rejeter(erreur)
    }
  })
}

export function nomFichierLot (intitule) {
  const base = String(intitule || '').trim().replace(/[^\w-]+/g, '_').replace(/^_|_$/g, '')
  return base ? 'Factures_' + base + '.pdf' : 'Factures.pdf'
}

/**
 * Déclenche le téléchargement du lot. Même contrat que l'export unitaire :
 * la promesse se résout quand le navigateur a pris la main.
 */
export function telechargerLotFacturesPdf (factures, parametres) {
  return new Promise(function (resoudre, rejeter) {
    try {
      const definition = construireDocDefinitionLot(factures, parametres)
      if (!definition) {
        resoudre()
        return
      }
      const pdfMake = chargerPdfMake()
      const nom = nomFichierLot(parametres && parametres.intitule)
      pdfMake.createPdf(definition).download(nom, function () { resoudre() })
    } catch (erreur) {
      rejeter(erreur)
    }
  })
}

export function nomFichier (facture) {
  const base = String(facture.numero || 'facture').replace(/[^\w-]+/g, '_')
  return base + '.pdf'
}

/**
 * Déclenche le téléchargement. Renvoie une promesse pour que l'appelant puisse
 * relâcher son indicateur de chargement, y compris en cas d'échec.
 */
export function telechargerFacturePdf (facture, totaux, qrDataUrl, logoDataUrl) {
  return new Promise(function (resoudre, rejeter) {
    try {
      const pdfMake = chargerPdfMake()
      const definition = construireDocDefinition(facture, totaux, qrDataUrl, logoDataUrl)
      pdfMake.createPdf(definition).download(nomFichier(facture), function () {
        resoudre()
      })
    } catch (erreur) {
      rejeter(erreur)
    }
  })
}

export default {
  construireDocDefinition,
  construireDocDefinitionLot,
  nomFichier,
  nomFichierLot,
  telechargerFacturePdf,
  telechargerLotFacturesPdf,
  factureEnBase64,
  lignesPiedEmetteur
}



// WEBPACK FOOTER //
// ./src/utils/facture/pdf.js